/**
 * CANONICAL MODULE — app-platform/shared/migrations.ts
 *
 * Copied byte-for-byte into every app (`src/lib/migrations.ts`). Pure: every byte of I/O is the
 * app's `MigrationIo` (for IndexedDB, `createIdbMigrationIo` in `idbBackup.ts`). Built for work order
 * W12 (task 2.b) from [design/W12-storage.md](../design/W12-storage.md) §3.2.
 *
 * **Fix bugs HERE, then copy outward. Never edit an app's copy in place.**
 *
 * ── The convention (quil's, now the estate's) ────────────────────────────────────────────────────
 * - An unindexed optional field needs no version bump.
 * - A new table or index does: a step with `reshapes: false`.
 * - A reshape is a version with `reshapes: true` AND a seeded-old-database test.
 * - Separate the copy and the drop into two versions (quil v10 copies, v11 drops), and list the
 *   dropped table in the drop step's `shrinks`.
 *
 * ── The runner, in order (`runMigration`) ────────────────────────────────────────────────────────
 * 1. **Probe** at the existing version, never upgrading. `version > target` → `NewerDataError`:
 *    written by a newer build; the caller opens read-only and writes nothing.
 * 2. **Backup** before any `reshapes` step, or whenever `unsynced > 0`. Additive steps on a fully
 *    synced store skip it (the remote is the copy). A backup counts only if it reports `complete`
 *    (`idbBackup.ts` writes its marker LAST, so a copy killed midway is never trusted).
 * 3. **Dry run** before any `reshapes` step (and on request): the chain is applied to a SCRATCH copy,
 *    censused and verified. A failure stops here with the real store untouched.
 * 4. **Apply** to the real store. A throw is re-probed: if rows went missing (a non-atomic store),
 *    the backup is restored.
 * 5. **Verify** the census again. Wrong but committed → restore the backup → `'restored'`.
 * 6. **Never delete a store on failure.** `MigrationIo` has no delete. The backup is kept.
 * 7. **No backup possible** (quota): `unsynced === 0` migrates anyway (the remote is the copy);
 *    `unsynced > 0` follows `policy.backupFailureWithUnsynced` (W12 decision 3), whose default,
 *    `'stay-read-only'`, does NOT migrate.
 *
 * ── Built-in invariants (on top of each step's `verify`) ─────────────────────────────────────────
 * After a run, no table has fewer rows than before unless a step lists it in `shrinks`, and the
 * unsynced count has not fallen: unsynced work is the one thing no migration may lose.
 *
 * ── As-built deviations from §3.2 ────────────────────────────────────────────────────────────────
 * - The dry run runs on its OWN scratch copy (`io.backup('dry-…')`, then `io.discard`), never on the
 *   backup: applying the chain to the backup would leave `restore` nothing old to restore.
 * - **A journal** (`io.readJournal`/`writeJournal`, kept beside the backup by `idbBackup.ts`) is
 *   written just before `apply` and cleared after verification. A tab killed between a committed
 *   apply and its verify would otherwise reopen at the new version looking `'current'`, with a
 *   wrong result nobody checked; the next run now verifies it against the journal and restores.
 * - A stale complete backup is never reused (`copyDatabase` replaces it), since it may predate
 *   edits made while an earlier run was held.
 */

/**
 * An IndexedDB object store's shape (keyPath, autoIncrement, every index), as idbBackup.ts copies
 * it with the rows and storageFixtures.ts builds an old-version store from it. Here rather than in
 * idbBackup.ts (moved 2026-11-01, task 8.3) so the test fixtures do not force an app with no
 * IndexedDB migrations to sync idbBackup.ts. Types only: no runtime cost.
 */
export interface IndexSchema {
  name: string
  keyPath: string | string[]
  unique: boolean
  multiEntry: boolean
}

export interface StoreSchema {
  name: string
  keyPath: string | string[] | null
  autoIncrement: boolean
  indexes: IndexSchema[]
}

/** Row counts per table, plus the unsynced count. `idsHash` is optional, for stores that track it. */
export interface Census {
  tables: Record<string, number>
  unsynced: number
  idsHash?: string
}

export interface MigrationStep<S = unknown> {
  from: number
  to: number
  name: string
  /** True when the step changes existing data (not just adds a table or index). */
  reshapes: boolean
  /** Tables this step may legitimately shrink (a drop after a copy). */
  shrinks?: readonly string[]
  /** For docs and kv state only; IndexedDB steps stay in Dexie's `.upgrade()`. */
  up?(state: S): S
  /** `null` = fine; a string fails the run with that reason. */
  verify?(before: Census, after: Census): string | null
}

export interface BackupHandle {
  name: string
  /** Only a complete backup is ever restored from. */
  complete: boolean
}

export interface MigrationIo {
  /** Opens WITHOUT upgrading. */
  probe(): Promise<{ version: number; census: Census; unsynced: number }>
  backup(tag: string): Promise<BackupHandle>
  /** Upgrade the real store (or, with `against`, that copy) to `target`. */
  apply(target: number, against?: BackupHandle): Promise<void>
  census(against?: BackupHandle): Promise<Census>
  restore(b: BackupHandle): Promise<void>
  /** Remove a scratch copy made for the dry run. Never called on the backup. */
  discard?(b: BackupHandle): Promise<void>
  /**
   * A durable note written just before `apply` and cleared after verification. A run killed between
   * the two finds it next time: the store is then at `to`, unverified, and is verified (and restored
   * from the journal's backup if wrong) before anything else happens. Optional, but every IndexedDB
   * store gets it from `createIdbMigrationIo`.
   */
  readJournal?(): Promise<MigrationJournal | null>
  writeJournal?(j: MigrationJournal | null): Promise<void>
}

export interface MigrationJournal {
  from: number
  to: number
  backup: BackupHandle
  before: Census
  at: string
}

export interface MigrationPolicy {
  /** W12 decision 3: a backup cannot be written while unsynced work exists. */
  backupFailureWithUnsynced: 'stay-read-only' | 'migrate-anyway'
}

/**
 * The recommended answer to decision 3: the safe default.
 * Exported for tests only (app-platform 8.3): no app code calls it.
 */
export const DEFAULT_MIGRATION_POLICY: Readonly<MigrationPolicy> = Object.freeze({
  backupFailureWithUnsynced: 'stay-read-only',
})

export type MigrationStatus =
  | 'current' // nothing to do
  | 'migrated' // applied and verified
  | 'dry-run-ok' // dryRun: true, and the chain verified on a scratch copy
  | 'dry-run-failed' // a verify failed on the scratch copy; the real store was not touched
  | 'held-read-only' // no backup and unsynced work: not migrated (decision 3 default)
  | 'restored' // applied, failed verification or threw, and the backup was restored
  | 'failed' // apply threw and nothing was lost (the store is still at `from`); backup kept

export interface MigrationReport {
  status: MigrationStatus
  from: number
  to: number
  steps: string[]
  backup: BackupHandle | null
  /** Why a run did not end `migrated` / `current` / `dry-run-ok`. */
  reason?: string
  before?: Census
  after?: Census
}

/** Data written by a newer build: open read-only and never write it. */
export class NewerDataError extends Error {
  readonly found: number
  readonly supported: number
  constructor(
    found: number,
    supported: number,
    message = `This device's copy was saved by a newer version (format ${found}; this build reads up to ${supported}). Reload to update.`,
  ) {
    super(message)
    this.found = found
    this.supported = supported
    this.name = 'NewerDataError'
  }
}

/** Exported for tests only (app-platform 8.3): no app code calls it. */
export class MigrationGapError extends Error {
  readonly from: number
  readonly to: number
  readonly missingAt: number
  constructor(from: number, to: number, missingAt: number) {
    super(`migrations: no step from version ${missingAt} on the way from ${from} to ${to}`)
    this.from = from
    this.to = to
    this.missingAt = missingAt
    this.name = 'MigrationGapError'
  }
}

export function isNewerDataError(e: unknown): e is NewerDataError {
  return e instanceof NewerDataError
}

/**
 * The contiguous chain from `from` to `to`, or `MigrationGapError`. `to < from` is `NewerDataError`.
 * Exported for tests only (app-platform 8.3): no app code calls it.
 */
export function planMigration<S>(steps: readonly MigrationStep<S>[], from: number, to: number): MigrationStep<S>[] {
  if (from > to) throw new NewerDataError(from, to)
  const out: MigrationStep<S>[] = []
  let v = from
  while (v < to) {
    const next = steps.find((s) => s.from === v)
    if (!next || next.to <= v || next.to > to) throw new MigrationGapError(from, to, v)
    out.push(next)
    v = next.to
  }
  return out
}

/**
 * The built-in invariants plus each step's `verify`. `null` = fine.
 * Exported for tests only (app-platform 8.3): no app code calls it.
 */
export function verifyCensus(plan: readonly MigrationStep<unknown>[], before: Census, after: Census): string | null {
  const shrinkable = new Set(plan.flatMap((s) => s.shrinks ?? []))
  for (const [table, n] of Object.entries(before.tables)) {
    if (shrinkable.has(table)) continue
    const now = after.tables[table] ?? 0
    if (now < n) return `table "${table}" went from ${n} rows to ${now}`
  }
  if (after.unsynced < before.unsynced) return `unsynced work went from ${before.unsynced} to ${after.unsynced}`
  for (const s of plan) {
    const problem = s.verify?.(before, after) ?? null
    if (problem) return `${s.name}: ${problem}`
  }
  return null
}

const sameCensus = (a: Census, b: Census): boolean => verifyCensus([], a, b) === null

export async function runMigration(
  io: MigrationIo,
  opts: { target: number; steps: readonly MigrationStep<unknown>[]; dryRun?: boolean; policy?: Partial<MigrationPolicy> },
): Promise<MigrationReport> {
  const policy: MigrationPolicy = { ...DEFAULT_MIGRATION_POLICY, ...opts.policy }
  const probe = await io.probe()
  const from = probe.version
  // 1. Newer data: refuse before anything else, and before any write.
  if (from > opts.target) throw new NewerDataError(from, opts.target)
  const base = { from, to: opts.target, before: probe.census }

  // 0. A run killed between apply and verify left its journal: settle that first.
  const journal = (await io.readJournal?.()) ?? null
  if (journal) {
    if (from === journal.to) {
      const after = await io.census()
      const plan = planMigration(opts.steps, journal.from, journal.to)
      const problem = verifyCensus(plan, journal.before, after)
      if (problem) {
        await io.restore(journal.backup)
        await io.writeJournal?.(null)
        return { from: journal.from, to: journal.to, before: journal.before, after, status: 'restored', steps: plan.map((s) => s.name), backup: journal.backup, reason: `interrupted run: ${problem}` }
      }
      await io.writeJournal?.(null)
      if (from === opts.target) {
        return { from: journal.from, to: journal.to, before: journal.before, after, status: 'migrated', steps: plan.map((s) => s.name), backup: journal.backup, reason: 'verified after an interrupted run' }
      }
    } else {
      // The apply never committed (IndexedDB's versionchange is atomic): the store is as it was.
      await io.writeJournal?.(null)
    }
  }

  if (from === opts.target) return { ...base, status: 'current', steps: [], backup: null }

  const plan = planMigration(opts.steps, from, opts.target)
  const names = plan.map((s) => s.name)
  const reshapes = plan.some((s) => s.reshapes)
  const unsynced = Math.max(probe.unsynced, probe.census.unsynced)

  // 2. Backup.
  let backup: BackupHandle | null = null
  if (reshapes || unsynced > 0) {
    let reason = ''
    try {
      backup = await io.backup(`pre-v${from}`)
      if (!backup.complete) {
        reason = `backup ${backup.name} did not complete`
        backup = null
      }
    } catch (e) {
      reason = `backup failed: ${e instanceof Error ? e.message : String(e)}`
      backup = null
    }
    if (!backup && unsynced > 0 && policy.backupFailureWithUnsynced === 'stay-read-only') {
      return { ...base, status: 'held-read-only', steps: names, backup: null, reason: `${reason}; ${unsynced} unsynced change(s) would have no copy` }
    }
  }

  // 3. Dry run, on its own scratch copy.
  if (reshapes || opts.dryRun) {
    let scratch: BackupHandle | null = null
    try {
      scratch = await io.backup(`dry-v${from}`)
      if (!scratch.complete) throw new Error(`scratch copy ${scratch.name} did not complete`)
      await io.apply(opts.target, scratch)
      const after = await io.census(scratch)
      const problem = verifyCensus(plan, probe.census, after)
      if (problem) return { ...base, status: 'dry-run-failed', steps: names, backup, reason: problem, after }
    } catch (e) {
      // A dry run that cannot even be made is a failure only when it was asked for or a reshape
      // needs it AND there is no backup to fall back on; otherwise the real run is still guarded by
      // its own verify + restore.
      if (opts.dryRun || !backup) {
        return { ...base, status: 'dry-run-failed', steps: names, backup, reason: e instanceof Error ? e.message : String(e) }
      }
    } finally {
      if (scratch) await io.discard?.(scratch).catch(() => undefined)
    }
    if (opts.dryRun) return { ...base, status: 'dry-run-ok', steps: names, backup }
  }

  // 4. Apply, with the journal written first wherever there is a backup to fall back on.
  if (backup) await io.writeJournal?.({ from, to: opts.target, backup, before: probe.census, at: new Date().toISOString() })
  try {
    await io.apply(opts.target)
  } catch (e) {
    const reason = `apply failed: ${e instanceof Error ? e.message : String(e)}`
    const now = await io.probe().catch(() => null)
    const intact = now !== null && now.version === from && sameCensus(probe.census, now.census)
    if (!intact && backup) {
      await io.restore(backup)
      await io.writeJournal?.(null)
      return { ...base, status: 'restored', steps: names, backup, reason }
    }
    if (intact) await io.writeJournal?.(null)
    return { ...base, status: 'failed', steps: names, backup, reason }
  }

  // 5. Verify.
  const after = await io.census()
  const problem = verifyCensus(plan, probe.census, after)
  if (problem) {
    if (backup) {
      await io.restore(backup)
      await io.writeJournal?.(null)
      return { ...base, status: 'restored', steps: names, backup, reason: problem, after }
    }
    // Only reachable with unsynced === 0 and no backup: the remote is the copy. Nothing is deleted.
    return { ...base, status: 'failed', steps: names, backup: null, reason: `${problem} (no backup; the remote copy is intact)`, after }
  }
  if (backup) await io.writeJournal?.(null)
  return { ...base, status: 'migrated', steps: names, backup, after }
}

// --------------------------------------------------------------------------------- documents

/**
 * Migrate a JSON document in memory (OneDrive docs, kv values). `versionOf` reads its version
 * (default: `schemaVersion`, falling back to `version`, else 0). A doc from a newer build throws
 * `NewerDataError` and must never be rewritten (`canWriteDoc`).
 */
export function migrateDoc<S>(
  doc: S,
  steps: readonly MigrationStep<S>[],
  target: number,
  opts: { versionOf?: (doc: S) => number; stamp?: (doc: S, version: number) => S } = {},
): { value: S; from: number; to: number; changed: boolean } {
  const versionOf = opts.versionOf ?? defaultDocVersion
  const from = versionOf(doc)
  const plan = planMigration(steps, from, target)
  let value = doc
  for (const s of plan) {
    if (s.up) value = s.up(value)
  }
  if (plan.length > 0) value = (opts.stamp ?? stampSchemaVersion)(value, target)
  return { value, from, to: target, changed: plan.length > 0 }
}

function defaultDocVersion(doc: unknown): number {
  if (!doc || typeof doc !== 'object') return 0
  const o = doc as { schemaVersion?: unknown; version?: unknown }
  if (typeof o.schemaVersion === 'number') return o.schemaVersion
  if (typeof o.version === 'number') return o.version
  return 0
}

function stampSchemaVersion<S>(doc: S, version: number): S {
  if (!doc || typeof doc !== 'object' || Array.isArray(doc)) return doc
  return { ...doc, schemaVersion: version }
}

/** May this build write over a doc whose stored version is `stored`? Never over a newer one. */
export function canWriteDoc(stored: number | null | undefined, buildVersion: number): boolean {
  return stored === null || stored === undefined || stored <= buildVersion
}
